import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Quote, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { BOOK_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

const USE_CASES = [
  "LinkedIn-videoer", "Produktpræsentationer", "Forklaring af komplekse emner", "Kundecases",
  "Nyheder fra virksomheden", "Onboarding af nye medarbejdere", "Intern kommunikation",
  "E-learning og undervisning", "Nyhedsbreve med video", "Kampagner", "Messer og events",
];

const STEPS = [
  { title: "Vi lærer din virksomhed indgående at kende", desc: "Vi sætter os ind i jeres forretning, målgrupper, budskaber, tone of voice og de situationer, hvor en AI-avatar skaber størst værdi." },
  { title: "Vi udvikler virksomhedens avatar", desc: "Vi skaber en professionel AI-avatar, der matcher jeres brand og fungerer som et naturligt, troværdigt og genkendeligt ansigt i kommunikationen." },
  { title: "I bruger den igen og igen", desc: "Når avataren er klar, kan den bruges til nye videoer, hver gang I har noget vigtigt at fortælle - uden nye optagedage." },
];

const BENEFITS = [
  "Et velkendt ansigt på jeres kommunikation",
  "Mere nærvær i jeres digitale kanaler",
  "Større genkendelse over tid",
  "En mere personlig måde at dele viden på",
  "Mulighed for at kommunikere oftere",
  "Ensartet kvalitet i jeres videoer",
  "Effektiv brug af virksomhedens eksperter og budskaber",
];

export default function AiAvatarPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — video-centred, editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-12 lg:gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
                  <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">AI Avatar</span>
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
                className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance">
                AI Avatar - <span style={{ color: TEAL }}>det velkendte ansigt på din virksomhed</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
                className="text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed max-w-xl">
                Mennesker skaber relationer til mennesker. Derfor husker vi langt bedre et ansigt end et logo. Med en AI-avatar får din virksomhed et genkendeligt ansigt, der kan formidle viden, fortælle historier og repræsentere virksomheden på tværs af jeres kommunikation.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: EASE }}
                className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
                <MButton href="#muligheder" size="lg" variant="secondary" data-testid="hero-muligheder">Se mulighederne</MButton>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">Mød Aicoms avatar</p>
              <div className="rounded-2xl border border-border bg-black overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 24px 60px -34px rgba(17,17,17,0.4)" }}>
                <video
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_pointtaken-ai/artifacts/2ozqgc48_Unknown.mp4"
                  poster="/avatar-poster.jpg"
                  controls playsInline preload="none"
                  className="w-full h-auto block"
                  data-testid="avatar-video">
                  Din browser understøtter ikke afspilning af video.
                </video>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Et ansigt gør virksomheden lettere at huske */}
      <section className="relative z-10 border-y border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-[0.4fr_0.6fr] gap-8 lg:gap-16">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Et ansigt gør virksomheden lettere at huske</h2>
          <div className="space-y-4 text-lg text-foreground/90 leading-relaxed max-w-2xl">
            <p>Virksomheder kommunikerer ofte gennem logoer, tekster, slides og kampagner. Men det er mennesker, vi lægger mærke til. Og det er mennesker, vi husker.</p>
            <p>En AI-avatar giver jeres virksomhed et fast ansigt udadtil - et ansigt, der kan gå igen på LinkedIn, i videoer, på hjemmesiden, i nyhedsbreve, i onboarding og i intern kommunikation.</p>
            <p className="text-foreground">Det skaber genkendelse. Og genkendelse er første skridt mod tillid.</p>
          </div>
        </div>
      </section>

      {/* Forskning quote */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24">
        <Quote className="h-8 w-8 text-muted-foreground/40 mb-5" />
        <p className="font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug text-balance">
          Forskning peger på, at digitale avatarer kan styrke engagement og tillid, når de bruges professionelt, med relevante budskaber og høj transparens. Den største værdi ligger ikke i teknologien i sig selv, men i virksomhedens evne til at være personlig, troværdig og konsistent i sin kommunikation.
        </p>
      </section>

      {/* Hvad er en AI-avatar */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad er det?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-6">Hvad er en AI-avatar?</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>En AI-avatar er en digital version af et ansigt, som kan repræsentere virksomheden i videoformat.</p>
            <p>Når avataren er udviklet, kan den bruges igen og igen til nye budskaber. I skriver manuskriptet - eller får hjælp af Aicom AI - og avataren formidler indholdet i en professionel video.</p>
            <p className="text-foreground">Det betyder, at jeres virksomhed kan være mere synlig, mere personlig og mere konsekvent i sin kommunikation.</p>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section id="muligheder" className="relative z-10 max-w-5xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Muligheder</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Hvad kan I bruge en AI-avatar til?</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">Dér, hvor et genkendeligt ansigt gør kommunikationen mere nærværende.</p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {USE_CASES.map((u, i) => (
            <motion.span key={u} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.3, delay: (i % 4) * 0.05 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-foreground" /> {u}
            </motion.span>
          ))}
        </div>
      </section>

      {/* Sådan hjælper vi jer */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Sådan hjælper vi jer</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra ansigt til færdig kommunikation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="text-5xl font-display font-medium text-muted-foreground/25">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-lg font-medium mt-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Derfor giver det mening + benefits */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Derfor giver det mening</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Ikke for at erstatte mennesker - men for at gøre kommunikationen skalerbar</h2>
          <p className="text-muted-foreground mt-4 leading-relaxed">AI-avatarer gør virksomhedens kommunikation mere genkendelig, mere personlig og mere skalerbar. Med en AI-avatar får I:</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {BENEFITS.map((b, i) => (
            <motion.div key={b} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.35, delay: (i % 2) * 0.06 }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4">
              <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0">
                <Check className="h-3.5 w-3.5 text-background" strokeWidth={2.5} />
              </div>
              <span className="text-foreground">{b}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Kombinér med Aicom AI */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex h-12 w-12 rounded-xl bg-secondary items-center justify-center mb-6">
            <Sparkles className="h-5 w-5 text-foreground" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-5">Kombinér AI Avatar med Aicom AI</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            <p>Den største værdi opstår, når AI Avatar kombineres med Aicom AI. Aicom AI finder de gode historier og skriver manuskripter, LinkedIn-opslag, nyhedsbreve, kampagner og pressebudskaber.</p>
            <p>AI-avataren omsætter derefter budskaberne til video med et genkendeligt ansigt - så I ikke bare får en avatar, men en mere sammenhængende måde at kommunikere på.</p>
          </div>
          <div className="mt-8">
            <MButton to="/losninger/aicom-ai" variant="secondary" data-testid="link-aicom-ai">Læs om Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
          </div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
